/**
 * Field — Form field layout that groups a label, input, description, and error
 * message with consistent spacing and accessibility.
 *
 * Field Component Guidelines
 * - Use Field to wrap any form input (Input, Textarea, Switch, Checkbox, etc.)
 *   that needs a label, description, or error message. It provides accessible
 *   structure and consistent spacing automatically.
 * - Use `orientation="horizontal"` for settings-style layouts where the label and
 *   control sit side by side (e.g. toggle rows). Use `orientation="responsive"` when
 *   you want horizontal on wide screens but stacked on narrow screens.
 * - Use `<FieldGroup>` when you have multiple related fields that should share
 *   consistent vertical spacing. Add `<FieldSeparator>` between logical sections.
 * - Use `<FieldSet>` + `<FieldLegend>` for semantically grouped controls like
 *   radio groups or checkbox groups — this is an accessibility requirement.
 * - Use `size="xs"` for dense, utility-style UIs; `size="sm"` for compact forms
 *   inside popovers or sidebars; `size="base"` for standard page-level forms.
 * - `<FieldError>` renders nothing when empty, so it's safe to always include in
 *   the layout — it only appears when there are actual validation errors.
 * @see https://ui.shadcn.com/docs/components/field shadcn/ui Field documentation
 */
import type { LabelSizes } from "@replohq/lattice/components/label/Label";
import type { VariantProps } from "class-variance-authority";

import * as React from "react";
import { useMemo } from "react";

import { Label } from "@replohq/lattice/components/label/Label";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        "gap-4 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3 flex flex-col",
        className,
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        "mb-1.5 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base",
        className,
      )}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        "gap-5 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4 group/field-group @container/field-group flex w-full flex-col",
        className,
      )}
      {...props}
    />
  );
}

const fieldVariants = cva(
  "data-[invalid=true]:text-error group/field flex w-full",
  {
    variants: {
      orientation: {
        vertical: "flex-col [&>*]:w-full [&>.sr-only]:w-auto",
        horizontal:
          "flex-row items-center [&>[data-slot=field-label]]:flex-auto has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        responsive:
          "flex-col [&>*]:w-full [&>.sr-only]:w-auto @md/field-group:flex-row @md/field-group:items-center @md/field-group:[&>*]:w-auto @md/field-group:[&>[data-slot=field-label]]:flex-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      },
      size: {
        xs: "gap-1",
        sm: "gap-1.5",
        base: "gap-2",
      },
    },
    defaultVariants: {
      orientation: "vertical",
      size: "base",
    },
  },
);

function Field({
  className,
  orientation = "vertical",
  size = "base",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation, size }), className)}
      {...props}
    />
  );
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        "gap-0.5 group/field-content flex flex-1 flex-col leading-snug",
        className,
      )}
      {...props}
    />
  );
}

function FieldLabel({
  size = "base",
  className,
  ...props
}: React.ComponentProps<typeof Label> & { size?: LabelSizes }) {
  return (
    <Label
      size={size}
      data-slot="field-label"
      UNSAFE_className={cn(
        "cursor-pointer has-data-checked:bg-sheet has-data-checked:border-default gap-2 group-data-[disabled=true]/field:opacity-50 has-[>[data-slot=field]]:rounded-lg has-[>[data-slot=field]]:border [&>*]:data-[slot=field]:p-2.5 group/field-label peer/field-label flex w-fit leading-snug",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col",
        className,
      )}
      {...props}
    />
  );
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        "gap-2 text-sm font-medium group-data-[disabled=true]/field:opacity-50 flex w-fit items-center leading-snug",
        className,
      )}
      {...props}
    />
  );
}

const fieldDescriptionVariants = cva(
  [
    "text-muted text-left [[data-variant=legend]+&]:-mt-1.5 leading-normal font-normal group-has-[[data-orientation=horizontal]]/field:text-balance",
    "last:mt-0 nth-last-2:-mt-1",
    "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4",
  ],
  {
    variants: {
      size: {
        xs: "text-2xs",
        sm: "text-xs",
        base: "text-sm",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

function FieldDescription({
  className,
  size = "base",
  ...props
}: React.ComponentProps<"p"> & VariantProps<typeof fieldDescriptionVariants>) {
  return (
    <p
      data-slot="field-description"
      className={cn(fieldDescriptionVariants({ size }), className)}
      {...props}
    />
  );
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-separator"
      data-content={Boolean(children)}
      className={cn(
        "-my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2 relative",
        className,
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="text-muted px-2 bg-sheet relative mx-auto block w-fit"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

const fieldErrorVariants = cva("text-error font-normal", {
  variants: {
    size: {
      xs: "text-2xs",
      sm: "text-xs",
      base: "text-sm",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

function FieldError({
  className,
  children,
  errors,
  size = "base",
  ...props
}: React.ComponentProps<"div"> & {
  errors?: ({ message?: string } | undefined)[];
} & VariantProps<typeof fieldErrorVariants>) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values(),
    ];

    if (uniqueErrors?.length === 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map((error, index) => {
          return error?.message && <li key={index}>{error.message}</li>;
        })}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn(fieldErrorVariants({ size }), className)}
      {...props}
    >
      {content}
    </div>
  );
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
};
